import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Button, Center, Separator, Flex, Heading, Spinner, Stack, VStack, Text, Box, Dialog } from '@chakra-ui/react';
import SectionWrapper from '@src/components/sco/LayoutBlocks/SectionWrapper';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { PaywalledAction } from '@src/components/sco/PaywalledAction';
import { usePerksContext } from '@src/components/sco/Perks/Layout/PerksContext';
import { PartnerProfileHero } from '@src/components/sco/Perks/PartnerProfileHero';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { Benefit } from '@src/graphql-types';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import React from 'react';
export const PartnerProfile = (props: {
  slug: string;
}) => {
  const perksContext = usePerksContext();
  const query = useQuery(gql`
      query ($slug: String) {
        benefits(where: { slug: $slug }) {
          id
          removeHeroOverlay
          name
          slug
          valueDescription
          redemptionUrl
          redemptionCode
          redemptionInstructions
          teaser
          tagline
          fullDescription {
            markdown
          }
          videoUrl
          legalTerms {
            markdown
          }
          heroImage {
            id
            url
          }
          cardImage {
            id
            url
          }
          screenshotImages {
            id
            url
          }
          logoImage {
            id
            url
          }
          categories {
            id
            slug
            name
            icon {
              id
              url
            }
          }
          facebookUrl
          twitterUrl
          youTubeUrl
          linkedInUrl
          instagramUrl
        }
      }
    `, {
    variables: {
      slug: props.slug
    }
  });
  const benefit = query.data?.benefits[0];
  useUniversalEffect(() => {
    perksContext.setBreadcrumbs([{
      title: 'Perks',
      href: '/perks'
    }, {
      title: 'Partners',
      href: '/perks/partners'
    }, {
      title: benefit?.name || '...',
      href: `/perks/partners/${benefit?.slug}`
    }]);
  }, [benefit]);
  if (query.loading || !query.data) {
    return <Center>
        <Spinner />
      </Center>;
  }
  const {
    getBreakpointValue
  } = useBreakpointContext();
  if (!benefit) {
    return <></>;
  }
  return <>
      <Flex align={'center'} justify={'center'} width={'full'}>
        <Stack align={'center'} direction={'column'} gap={0} width={'full'}>
          {/* Hero */}
          <PartnerProfileHero benefit={benefit} />
          <SectionWrapper>
            <Stack align={'flex-start'} direction={getBreakpointValue(['column-reverse', 'column-reverse', 'column-reverse', 'row'])} gap={getBreakpointValue([5, 5, 10])} justify={'flex-start'} width={'full'}>
              <VStack gap={getBreakpointValue([5, 5, 10])} width={'full'}>
                <VStack align={'flex-start'} bgColor={'colorPalette.muted'} borderRadius={'xl'} boxShadow={'sm'} gap={5} padding={10} width={'full'}>
                  <Heading size={'3xl'}>About</Heading>
                  <MarkdownRenderer children={benefit.fullDescription?.markdown} />
                </VStack>
                {benefit.legalTerms?.markdown && <VStack align={'flex-start'} bgColor={'colorPalette.muted'} borderRadius={'xl'} boxShadow={'sm'} gap={5} padding={10} width={'full'}>
                    <MarkdownRenderer children={benefit.legalTerms?.markdown} />
                  </VStack>}
              </VStack>
              {(() => {
              switch (benefit.slug) {
                case 'bizplan':
                  return <VStack bgColor={'colorPalette.muted'} borderRadius={'xl'} boxShadow={'sm'} gap={5} padding={10} position={getBreakpointValue(['static', 'static', 'sticky'])} top={getBreakpointValue(['0', '0', '96px'])} width={getBreakpointValue(['full', 'full', 'full', '50%'])}>
                        <Text textStyle={'badgeSm'}>Benefit Value</Text>
                        <Heading size={'3xl'} textAlign={'center'}>
                          {benefit.valueDescription}
                        </Heading>
                        <Separator />
                        <Box width={'full'}>
                          <PaywalledAction contentPermission={'canAccessPremiumBenefits'} variant={'button-only'}>
                            <RedeemButton benefit={benefit} />
                          </PaywalledAction>
                        </Box>
                      </VStack>;
                case 'clarity-fm':
                  return <VStack bgColor={'colorPalette.muted'} borderRadius={'xl'} boxShadow={'sm'} gap={5} padding={10} position={getBreakpointValue(['static', 'static', 'sticky'])} top={getBreakpointValue(['0', '0', '96px'])} width={getBreakpointValue(['full', 'full', 'full', '50%'])}>
                        <Text textStyle={'badgeSm'}>Benefit Value</Text>
                        <Heading size={'3xl'} textAlign={'center'}>
                          {benefit.valueDescription}
                        </Heading>
                        <Separator />
                        <Box width={'full'}>
                          <PaywalledAction contentPermission={'canAccessPremiumBenefits'} variant={'button-only'}>
                            <Button asChild size={'lg'} variant={'solid'} width={'full'}>
                              <NextLink href={`${benefit.redemptionUrl}`} target={'_blank'}>
                                Redeem Benefit
                              </NextLink>
                            </Button>
                          </PaywalledAction>
                        </Box>
                      </VStack>;
                case 'launchrock':
                  return <VStack bgColor={'colorPalette.muted'} borderRadius={'xl'} boxShadow={'sm'} gap={5} padding={10} position={getBreakpointValue(['static', 'static', 'sticky'])} top={getBreakpointValue(['0', '0', '96px'])} width={getBreakpointValue(['full', 'full', 'full', '50%'])}>
                        <Text textStyle={'badgeSm'}>Benefit Value</Text>
                        <Heading size={'3xl'} textAlign={'center'}>
                          {benefit.valueDescription}
                        </Heading>
                        <Separator />
                        <Box width={'full'}>
                          <PaywalledAction contentPermission={'canAccessPremiumBenefits'} variant={'button-only'}>
                            <RedeemButton benefit={benefit} />
                          </PaywalledAction>
                        </Box>
                      </VStack>;
                case 'fundable':
                  return <VStack bgColor={'colorPalette.muted'} borderRadius={'xl'} boxShadow={'sm'} gap={5} padding={10} position={getBreakpointValue(['static', 'static', 'sticky'])} top={getBreakpointValue(['0', '0', '96px'])} width={getBreakpointValue(['full', 'full', 'full', '50%'])}>
                        <Text textStyle={'badgeSm'}>Benefit Value</Text>
                        <Heading size={'3xl'} textAlign={'center'}>
                          {benefit.valueDescription}
                        </Heading>
                        <Separator />
                        <Box width={'full'}>
                          <PaywalledAction contentPermission={'canAccessPremiumBenefits'} variant={'button-only'}>
                            <RedeemButton benefit={benefit} />
                          </PaywalledAction>
                        </Box>
                      </VStack>;
                default:
                  return <VStack bgColor={'colorPalette.muted'} borderRadius={'xl'} boxShadow={'sm'} gap={5} padding={10} position={getBreakpointValue(['static', 'static', 'sticky'])} top={getBreakpointValue(['0', '0', '96px'])} width={getBreakpointValue(['full', 'full', 'full', '50%'])}>
                        <Text textStyle={'badgeSm'}>Benefit Value</Text>
                        <Heading size={'3xl'} textAlign={'center'}>
                          {benefit.valueDescription}
                        </Heading>
                        <Separator />
                        <Box width={'full'}>
                          <PaywalledAction contentPermission={'canAccessPremiumBenefits'} variant={'button-only'}>
                            <RedeemButton benefit={benefit} />
                          </PaywalledAction>
                        </Box>
                      </VStack>;
              }
            })()}
            </Stack>
          </SectionWrapper>
        </Stack>
      </Flex>
    </>;
};
const RedeemButton = props => {
  const $ = _c(13);
  const [isOpen, setIsOpen] = React.useState(false);
  const t0 = `${props.benefit.redemptionUrl}`;
  let t1;
  if ($[0] !== props.benefit.redemptionUrl) {
    t1 = ev => {
      if (!props.benefit.redemptionUrl) {
        ev.preventDefault();
        setIsOpen(true);
      }
    };
    $[0] = props.benefit.redemptionUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = <Button asChild={true} size="lg" variant="solid" width="full"><NextLink href={t0} target="_blank" onClick={t1}>Redeem Benefit</NextLink></Button>;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isOpen || $[6] !== props.benefit.redemptionCode || $[7] !== props.benefit.redemptionInstructions || $[8] !== props.benefit.redemptionUrl) {
    t3 = !props.benefit.redemptionUrl && <Dialog.Root open={isOpen} onOpenChange={t4 => {
      const {
        open
      } = t4;
      if (!open) {
        setIsOpen(false);
      }
    }}><Dialog.Backdrop /><Dialog.Positioner><Dialog.Content bgColor="colorPalette.muted"><Dialog.CloseTrigger /><Dialog.Header><Heading>How to Redeem</Heading></Dialog.Header><Dialog.Body>{props.benefit.redemptionCode && <Text borderBottom="1px solid rgba(255,255,255,.1)" paddingBottom="0.5em">{"Use this coupon code: "}<code>{props.benefit.redemptionCode}</code></Text>}<Box paddingY="0.5em"><MarkdownRenderer>{props.benefit.redemptionInstructions}</MarkdownRenderer></Box></Dialog.Body></Dialog.Content></Dialog.Positioner></Dialog.Root>;
    $[5] = isOpen;
    $[6] = props.benefit.redemptionCode;
    $[7] = props.benefit.redemptionInstructions;
    $[8] = props.benefit.redemptionUrl;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};